import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

import Login from './components/Login';
import Unauthorized from './components/Unauthorized';
import PasswordReset from './components/PasswordReset';
import NezoSso from './components/NezoSso';

import routes from './routes';

import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { RegionProvider, useRegion } from './context/RegionContext';
import RoleLayout from './context/RoleLayout';
import BunningsDataPage from './components/APIs/BunningsDataPage';
import PlacemakersDataPage from './components/APIs/PlacemakersDataPage';
import { useState, useEffect } from 'react';
import RateLimitBypassModal from './components/RateLimitBypassModal';
import { setShowModalCallback } from './services/api';
import SessionTracker from './components/SessionTracker';


// Inner component that has access to RegionContext
function AppRoutes() {
  const { selectedRegion } = useRegion();

  return (
     <>
    <SessionTracker />
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<PasswordReset />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      {/* Nezo sign-on: /sso?code=… from the Nezo gateway (see components/NezoSso.js) */}
      <Route path="/sso" element={<NezoSso />} />

      {/* Pending approval routes (no layout, no role check) */}
      <Route
        path="/supplier/pending-approval"
        element={
          <ProtectedRoute requiresAuth={true} skipRoleCheck={true}>
            {routes.find(r => r.path === '/supplier/pending-approval')?.element}
          </ProtectedRoute>
        }
      />
      <Route
        path="/user/pending-approval"
        element={
          <ProtectedRoute requiresAuth={true} skipRoleCheck={true}>
            {routes.find(r => r.path === '/user/pending-approval')?.element}
          </ProtectedRoute>
        }
      />

      {/* All other protected routes */}
      {routes
        .filter(route => !route.path.includes('/pending-approval'))
        .map((route, index) => {
          // Special handling for Bunnings route - inject selectedRegion prop
          let element = route.element;
          if (route.path === '/bunnings-routes') {
            element = <BunningsDataPage selectedRegion={selectedRegion} />;
          } else if (route.path === '/placemakers') {
            element = <PlacemakersDataPage selectedRegion={selectedRegion} />;
          }

          return (
            <Route
              key={index}
              path={route.path}
              element={
                <ProtectedRoute
                  // default to all roles if not specified on the route
                  allowedRoles={route.allowedRoles || ['admin', 'user', 'supplier']}
                  requiresAuth={route.protected !== false} // treat undefined as protected
                  requiresApproval={['/user/dashboard','/supplier/dashboard'].includes(route.path)}
                >
                  <RoleLayout>
                    <div className="container">{element}</div>
                  </RoleLayout>
                </ProtectedRoute>
              }
            />
          );
        })}
    </Routes>
    </>
  );
}

function App() {
  const [showBypassModal, setShowBypassModal] = useState(false);

  useEffect(() => {
    // Register the callback
    setShowModalCallback(setShowBypassModal);
    console.log('✅ Modal callback registered');
  }, []);

  const handleBypassSuccess = () => {
    console.log('✅ Bypass successful - reloading page');
    setShowBypassModal(false);
    window.location.reload();
  };

  const handleBypassCancel = () => {
    console.log('❌ Bypass cancelled');
    setShowBypassModal(false);
  };

  return (
    <>
    <RateLimitBypassModal
        show={showBypassModal}
        onSuccess={handleBypassSuccess}
        onCancel={handleBypassCancel}
      />
    <RegionProvider>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </RegionProvider>
    </>
  );
}

export default App;
